import type { ICellRendererParams } from "ag-grid-community";
import { memo, useMemo } from "react";
import { Skeleton } from "@/components/ui/feedback/skeleton";
import { cn } from "@/lib/utils";
import { addCommasToNumbers } from "@/lib/utils/prettyName";
import type { BroadcastClipRowData } from "./BroadcastClipsTableStates";

type NumberFormat = "commas" | "none";

interface BroadcastClipCellRendererProps {
  params: ICellRendererParams<BroadcastClipRowData>;
  numberFormat?: NumberFormat;
  formatter?: (value: unknown) => string;
  valueClassName?: string;
  wrapText?: boolean;
}

const BroadcastClipCellRenderer = ({
  params,
  numberFormat = "none",
  formatter,
  valueClassName,
  wrapText = false,
}: BroadcastClipCellRendererProps) => {
  const { value, data } = params;

  const formattedValue = useMemo(() => {
    if (formatter) return formatter(value);
    if (value == null) return "-";
    if (typeof value === "number") {
      switch (numberFormat) {
        case "commas":
          return addCommasToNumbers(value);
        default:
          return String(value);
      }
    }
    return String(value);
  }, [value, numberFormat, formatter]);

  if (!data) {
    return <Skeleton className="h-5 w-full align-middle mt-1.5" />;
  }

  const contentClassName = cn(
    valueClassName,
    wrapText
      ? "wrap-break-word leading-relaxed"
      : "overflow-hidden text-ellipsis whitespace-nowrap",
  );

  return (
    <div
      className={cn("flex min-w-0", wrapText ? "items-start" : "items-center")}
    >
      <span className={contentClassName}>{formattedValue}</span>
    </div>
  );
};

BroadcastClipCellRenderer.displayName = "BroadcastClipCellRenderer";

export default memo(BroadcastClipCellRenderer);
